import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";

import { routeTree } from "./routeTree.gen";
// Self-hosted Geist, replacing `next/font/google`. Not a new capability -- the app has always
// used this typeface; `next/font` fetched and self-hosted it at build time, and that machinery
// goes away with Next. The variable font is one file rather than a weight per request.
//
// It must be imported **before** `styles.css`, which reads `--font-sans` in its `@theme inline`
// block. The other order leaves the variable unset and the whole app renders in the browser
// default -- a change nobody asked for, arriving silently.
import "@fontsource-variable/geist";
import "./styles.css";
import { initDatadogRum } from "@/components/datadog-rum";

// **Before `createRoot`, and that ordering is load-bearing.** `datadogRum.init()` is what patches
// `window.fetch`, and the app's very first request is `AuthProvider`'s `/v1/me`, fired by
// `useQuery` the moment React commits. Initialising from inside a component's `useEffect` -- which
// is how this worked when the app was Next -- gives no ordering guarantee between the two, so
// whether that first request carries `traceparent` would depend on where the tag sat among its
// siblings. Nothing would error; the RUM-to-APM join would just quietly miss the most useful
// request in the session.
//
// It also puts the error handler in place before `createRouter`, the route tree and the first
// render, so a boot failure is reported rather than producing zero events -- which is
// indistinguishable from RUM not being wired up at all, the state this app was in from
// 2026-09-05 until this commit.
//
// Not literally the first line: the two side-effect imports above still run first, because ESM
// hoists imports. "Before React starts" is the claim, and that is what matters here.
initDatadogRum();

const router = createRouter({
  routeTree,
  // A 401 from the API means the session is gone, and the SPA's response is a full navigation to
  // `/v1/auth/login` rather than a client-side route change -- so there is no "not found" state
  // worth special-casing beyond a genuinely unknown path.
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  // Registering the router type is what makes `Link` and `navigate` typed against the real route
  // tree -- typos in a path become compile errors rather than a blank page. It is the reason
  // TanStack Router was chosen over React Router, so it is wired from the first commit.
  interface Register {
    router: typeof router;
  }
}

const container = document.getElementById("root");
if (!container) {
  // `index.html` is ours and always has this element, so reaching here means the HTML shell was
  // replaced or truncated. Failing loudly beats rendering nothing and looking like a blank page.
  throw new Error("#root is missing from index.html");
}

createRoot(container).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);
